import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CATEGORY_BANNER_EYEBROW } from "./constants";
import { trackAdvertiser } from "./trackAdvertiser";

export function SponsorBanner({ advertiser, onInquire, compact }) {
  const advertiserId = advertiser?.id;
  const tracked = useRef(false);
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    if (!advertiserId || tracked.current) return;
    tracked.current = true;
    trackAdvertiser(advertiserId, "view");
  }, [advertiserId]);
  if (!advertiser) return null;
  const hasLink = Boolean(advertiser.destination_url);
  const handleClick = () => {
    trackAdvertiser(advertiser.id, "click");
    if (!hasLink) onInquire?.(advertiser);
  };
  const eyebrow = CATEGORY_BANNER_EYEBROW[advertiser.category] || "Featured Partner";
  const linkProps = hasLink ? { href: advertiser.destination_url, target: "_blank", rel: "noopener noreferrer" } : {};
  const Tag = hasLink ? "a" : "button";
  const profileUrl = advertiser.slug ? `/local-experts/${advertiser.slug}` : null;
  const trackView = () => trackAdvertiser(advertiser.id, "click");
  const image = !imgError && (advertiser.cover_photo_url || advertiser.logo_url);

  if (compact) {
    return (
      <div data-testid={`sponsor-banner-${advertiser.id}`} className="rounded-xl overflow-hidden bg-[#111820] flex items-center gap-3 p-2.5">
        {profileUrl ? (
          <Link to={profileUrl} onClick={trackView} className="flex items-center gap-3 flex-1 min-w-0">
            {image && <img src={image} alt="" onError={() => setImgError(true)} className="h-10 w-10 rounded-lg object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <span className="block text-[9px] font-mono uppercase tracking-[.1em] text-[#C7A361]">{eyebrow}</span>
              <p className="text-sm text-white leading-tight truncate">{advertiser.business_name}</p>
              {advertiser.headline && <p className="text-xs text-white/60 leading-tight truncate">{advertiser.headline}</p>}
            </div>
          </Link>
        ) : (
          <div className="flex-1 min-w-0">
            <span className="block text-[9px] font-mono uppercase tracking-[.1em] text-[#C7A361]">{eyebrow}</span>
            <p className="text-sm text-white leading-tight truncate">{advertiser.business_name}</p>
          </div>
        )}
        <Tag data-testid={`sponsor-banner-cta-${advertiser.id}`} {...linkProps} onClick={handleClick} className="inline-flex items-center gap-1 rounded-full bg-[#C7A361] text-[#111820] text-[11px] font-semibold uppercase tracking-[0.06em] px-3.5 py-2 whitespace-nowrap hover:brightness-95 transition shrink-0">{advertiser.button_label || "Get Pre-Qualified"}<ArrowRight size={11} /></Tag>
      </div>
    );
  }
  return (
    <div data-testid={`sponsor-banner-${advertiser.id}`} className="rounded-xl overflow-hidden bg-[#111820]">
      <div className="flex flex-col sm:flex-row items-stretch">
        {image && (
          profileUrl ? (
            <Link to={profileUrl} onClick={trackView} className="block w-full sm:w-64 h-36 sm:h-auto flex-shrink-0">
              <img src={image} alt="" onError={() => setImgError(true)} className="w-full h-full object-cover" />
            </Link>
          ) : <img src={image} alt="" onError={() => setImgError(true)} className="w-full sm:w-64 h-36 sm:h-auto object-cover flex-shrink-0" />
        )}
        <div className="flex-1 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-mono uppercase tracking-[.14em] text-[#C7A361]">{eyebrow}</span>
            {profileUrl ? (
              <Link to={profileUrl} onClick={trackView} className="block font-serif-display text-xl mt-1.5 text-white hover:text-[#C7A361] transition-colors">{advertiser.business_name}</Link>
            ) : <p className="font-serif-display text-xl mt-1.5 text-white">{advertiser.business_name}</p>}
            {advertiser.headline && <p className="text-sm mt-1 text-white/70">{advertiser.headline}</p>}
            {advertiser.description && <p className="text-sm mt-1.5 text-white/70">{advertiser.description}</p>}
            {advertiser.disclosure_text && <p className="text-[10px] text-white/40 mt-1.5">{advertiser.disclosure_text}</p>}
          </div>
          <Tag data-testid={`sponsor-banner-cta-${advertiser.id}`} {...linkProps} onClick={handleClick} className="inline-flex items-center justify-center rounded-full bg-[#C7A361] text-[#111820] text-xs font-semibold uppercase tracking-[0.08em] px-5 py-2.5 whitespace-nowrap hover:brightness-95 transition">{advertiser.button_label || "Get Pre-Qualified"}</Tag>
        </div>
      </div>
    </div>
  );
}
